// WS listener da feature stores. Recebe a action `inbox/presence.changed`
// despachada pelo wsMiddleware (namespace /inbox) e aplica um patch cirúrgico
// no cache do RTK Query — 0 rede, status do card de equipe muda na hora.
//
// Setup: registrado uma única vez no boot via attachStorePresenceListener().
// O patch substitui o antigo polling de 30s do TeamTab (push em tempo real).
import type { AnyAction } from '@reduxjs/toolkit';
import { listenerMiddleware } from '@/app/store/middleware';
import { storesApi } from './api';

// listenerMiddleware genérico expõe Dispatch<UnknownAction> que não aceita os
// ThunkActions tipados de createApi sem ciclo de tipos. Mesmo escape pragmático
// usado em features/inbox/ws-listeners.ts — runtime idêntico; a action é
// validada pelo próprio storesApi.util.*.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseDispatch = (action: any) => any;
function looseDispatch(api: { dispatch: unknown }): LooseDispatch {
  return api.dispatch as LooseDispatch;
}

interface PresenceChangedPayload {
  userId: string;
  online: boolean;
}

/**
 * Registra o listener de presença das lojas. Patcha o campo `online` do membro
 * em TODAS as listas `listStoreMembers` em cache (uma por storeId). Idempotente
 * o suficiente — chamar 1× no boot.
 */
export function attachStorePresenceListener(): void {
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/presence.changed',
    effect: (action, listenerApi) => {
      const payload = (action as AnyAction & { payload?: PresenceChangedPayload }).payload;
      if (!payload?.userId || typeof payload.online !== 'boolean') return;

      const d = looseDispatch(listenerApi);
      // listenerApi.getState() é `unknown` (AppStartListening usa state genérico
      // pra evitar ciclo store ↔ middleware). Cast pro RootState que o seletor
      // espera — mesmo escape pragmático do looseDispatch acima.
      const state = listenerApi.getState() as Parameters<
        typeof storesApi.util.selectInvalidatedBy
      >[0];
      const entries = storesApi.util.selectInvalidatedBy(state, [{ type: 'StoreMember' }]);
      for (const { endpointName, originalArgs } of entries) {
        if (endpointName !== 'listStoreMembers') continue;
        d(
          storesApi.util.updateQueryData(
            'listStoreMembers',
            originalArgs as { storeId: string; includeInactive?: boolean },
            (draft) => {
              const m = draft.find((x) => x.userId === payload.userId);
              if (m) m.online = payload.online;
            },
          ),
        );
      }
    },
  });
}
